import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { useEffect, useState } from 'react';
import { useForm, useController, useFormState, Controller, SubmitHandler } from 'react-hook-form';
import MaskedInput, { conformToMask } from 'react-text-mask';
import { z } from 'zod';

import LoadingSvg from '@/components/common/LoadingSvg';
import {
    addressAutoCompleteCheckoutFind,
    addressAutoCompleteCheckoutRetrieve,
} from '@/external/service/canadapostaddress.service';
import { TmAddress } from '@/tscapi/model/account';

import { useAddress } from '../Context/AddressContext';
import { useAddress as useAddressQuery, useUpdateAddress } from '../hooks/useAddress';
import { AddressSchema, province } from '../schemas/address.schema';
import { formatAddress } from '../utils/address.utils';
interface FormTscCardProps {
    selectedAddress?: TmAddress;
    setFormError?: React.Dispatch<React.SetStateAction<any>>;
    selectedAddressType?: string;
}

type ValidationAddressSchema = z.infer<typeof AddressSchema>;
type Province = z.infer<typeof AddressSchema>['province'];
function upperCasePipe(conformedValue) {
    return conformedValue.toUpperCase();
}
const phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
const postalCodeMask = [/[A-Z]/i, /\d/, /[A-Z]/i, ' ', /\d/, /[A-Z]/i, /\d/];
export const FormAddress = ({ selectedAddress, setFormError, selectedAddressType }: FormTscCardProps) => {
    const defaultValues = {
        firstname: selectedAddress?.FirstName || '',
        lastname: selectedAddress?.LastName || '',
        dayphone: conformToMask(selectedAddress?.DayPhone, phoneNumberMask, { guide: false }).conformedValue || '',
        address: selectedAddress?.Street || '',
        address2: selectedAddress?.AddressRef1 || '',
        province: (selectedAddress?.State || 'a') as Province,
        postalcode: conformToMask(selectedAddress?.ZipCode, postalCodeMask, { guide: false }).conformedValue || '',
        city: selectedAddress?.City || '',
        defaultShipping: selectedAddress?.IsDefault !== undefined ? selectedAddress?.IsDefault : false,
        defaultBilling: false,
    };
    const isMutating = useIsMutating();
    const { setMode, setMode2, updateAccordionAtIndex, setShowNotification, setShowWarning } = useAddress();
    const {
        handleSubmit,
        control,
        register,
        setError,
        trigger,
        watch,
        setValue,
        getFieldState,
        clearErrors,
        formState: { errors, isValid, isSubmitting },
    } = useForm<ValidationAddressSchema>({
        resolver: zodResolver(AddressSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        criteriaMode: 'all',
        defaultValues,
    });

    const mutation = useAddressQuery();
    const updateMutation = useUpdateAddress();

    const province = [
        { name: 'Alberta', id: 'AB' },
        { name: 'British Columbia', id: 'BC' },
        { name: 'Manitoba', id: 'MB' },
        { name: 'New Brunswick', id: 'NB' },
        { name: 'Newfoundland and Labrador', id: 'NL' },
        { name: 'Northwest Territories', id: 'NT' },
        { name: 'Nova Scotia', id: 'NS' },
        { name: 'Nunavut', id: 'NU' },
        { name: 'Ontario', id: 'ON' },
        { name: 'Prince Edward Island', id: 'PE' },
        { name: 'Quebec', id: 'QC' },
        { name: 'Saskatchewan', id: 'SK' },
        { name: 'Yukon Territory', id: 'YT' },
    ];

    interface CanadaPostAddressData {
        Cursor?: number;
        Description: string;
        Highlight?: string;
        Id: string;
        Next?: string;
        Text: string;
    }
    //maybe remove this once phone and postal default value is fixed
    useEffect(() => {
        if (selectedAddress != null) {
            trigger();
        }
    }, [trigger, selectedAddress]);
    const { onChange: onProvinceChange, ...provinceField } = register('province');
    // autocomplete components
    const [addressList, setAddressList] = useState<CanadaPostAddressData[]>([]);
    // const [selectedItem, setSelectedItem] = useState();
    const {
        field: { ...field },
        fieldState,
    } = useController({
        name: 'address',
        control,
    });
    const {
        isOpen,
        openMenu,
        getLabelProps,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        setInputValue,
        selectedItem,
    } = useCombobox({
        items: addressList,
        itemToString(item) {
            return item ? `${item.Text}` : '';
        },
        onSelectedItemChange: ({ inputValue, selectedItem }) => {
            // check canpost type and if find, get new set
            if (selectedItem.Id == 'optOut') {
                setValue('city', '');
                setValue('province', 'a');
                setValue('postalcode', '');
            } else if (selectedItem.Next == 'Retrieve') {
                //clear form fields for street,city,state,zip
                retrieveAddress(selectedItem.Id);
                return;
            } else if (selectedItem && selectedItem.Next == 'Find') {
                findAddress(inputValue, selectedItem.Id);
                openMenu();
                return;
            } else {
                field.onChange(inputValue);
            }
        },
        onInputValueChange({ inputValue }) {
            // onChange(inputValue);
            if (inputValue.length > 1) {
                findAddress(inputValue, undefined);
            }
            //setInputValue(inputValue);
            //setItems(books.filter(getBooksFilter(inputValue)));
        },
    });

    const findAddress = async (value, lastId) => {
        let streetAddresses: CanadaPostAddressData[] = [];

        try {
            const response = await addressAutoCompleteCheckoutFind(value, lastId);
            if (response && response.status == 200) {
                if (response.data.Items && response.data.Items.length > 0 && response.data.Items[0].Error) {
                    streetAddresses = [];
                } else {
                    streetAddresses = response.data.Items;
                    streetAddresses.push({
                        Text: "I don't see my address. Let me type it in.",
                        Description: '',
                        Id: 'optOut',
                        Next: '',
                    });
                }
                setAddressList(streetAddresses);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };
    const retrieveAddress = async (id) => {
        try {
            const response = await addressAutoCompleteCheckoutRetrieve(id);
            let selectedAddress;
            if (response && response.status == 200) {
                selectedAddress = response.data.Items[0] as CanadaPostAddressData;
                field.onChange(selectedAddress.Line1);
                setValue('address2', selectedAddress.Line2);
                setValue('city', selectedAddress.City);
                setValue('province', selectedAddress.Province);
                setValue('postalcode', selectedAddress.PostalCode);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };

    const onSubmit: SubmitHandler<ValidationAddressSchema> = (data) => {
        //prepare payload for api call
        const addressPayload = formatAddress(data);
        if (selectedAddress == null) {
            // adding new address
            mutation.mutate(addressPayload, {
                onSuccess: () => {
                    setMode('view');
                    setMode2('view');
                    updateAccordionAtIndex(0, true);
                    updateAccordionAtIndex(1, true);
                    updateAccordionAtIndex(2, false);
                    setShowWarning(false);
                    setShowNotification(true);
                },
                onError: (error) => {
                    setFormError(error as string);
                },
            });
        } else {
            // updating existing address
            const payload = {
                address: addressPayload,
                id: selectedAddress.Id,
                type: selectedAddressType,
            };
            payload.address.Id = selectedAddress.Id;
            updateMutation.mutate(payload, {
                onSuccess: () => {
                    setMode('view');
                    setMode2('view');
                    updateAccordionAtIndex(0, true);
                    updateAccordionAtIndex(1, true);
                    updateAccordionAtIndex(2, false);
                    setShowWarning(false);
                    setShowNotification(true);
                },
                onError: (error) => {
                    setFormError(error as string);
                },
            });
        }
    };
    return (
        <>
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-7 mt-6">Required fields are marked with an asterisk (*)</div>
                <div className="grid grid-cols-1 gap-0 md2:grid-cols-2 md2:gap-5">
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            maxLength={16}
                            placeholder=" "
                            data-error={getFieldState('firstname').error ? 'true' : 'false'}
                            // data-empty={firstnameWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="given-name"
                            {...register('firstname')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="firstname"
                        >
                            First name *
                        </label>
                        {errors.firstname && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.firstname.message}</div>
                        )}
                    </div>
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={16}
                            data-error={getFieldState('lastname').error ? 'true' : 'false'}
                            // data-empty={lastnameWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="family-name"
                            {...register('lastname')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="lastname"
                        >
                            Last name *
                        </label>
                        {errors.lastname && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.lastname.message}</div>
                        )}
                    </div>
                </div>
                <div className="z-1 relative mb-6">
                    <div className="relative">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={30}
                            {...getInputProps({
                                ...field,
                                onChange: (event) => {
                                    field.onChange(event as React.ChangeEvent<HTMLInputElement>);
                                },
                            })}
                            data-error={getFieldState('address').error ? 'true' : 'false'}
                            // data-empty={addressWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="billing address-line1"
                            data-qm-encrypt="true"
                        />
                        <label
                            {...getLabelProps()}
                            className="pointer-events-none absolute top-2.5 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                            htmlFor="address"
                        >
                            Street and house number *
                        </label>
                        <ul
                            className={`absolute z-10 max-h-80 w-full overflow-y-auto border border-solid border-[#e3e3e3] bg-white p-0 px-1 py-1 shadow-md ${
                                !(isOpen && addressList.length) && 'hidden'
                            }`}
                            {...getMenuProps()}
                        >
                            {isOpen &&
                                addressList.map((item, index) => (
                                    <li
                                        className={classNames(
                                            highlightedIndex === index && 'bg-gray-200',
                                            selectedItem === item && 'font-bold',
                                            'z-10 flex flex-col px-3 py-2 shadow-sm',
                                        )}
                                        key={item.Id}
                                        {...getItemProps({ item, index })}
                                    >
                                        <span className="text-sm text-gray-700">
                                            {item.Text} {item.Description}
                                        </span>
                                    </li>
                                ))}
                        </ul>
                    </div>
                    {errors.address && <div className="mt-1 text-xs text-[#df0c0c]">{errors.address.message}</div>}
                </div>
                <div className="relative z-0 mb-6">
                    <input
                        type="text"
                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                        placeholder=" "
                        maxLength={30}
                        data-error={getFieldState('address2').error ? 'true' : 'false'}
                        // data-empty={addressWatch === '' ? 'true' : 'false'}
                        tabIndex={0}
                        {...register('address2')}
                        data-qm-encrypt="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                        htmlFor="address2"
                    >
                        Apartment, suite, etc
                    </label>
                    {errors.address2 && <div className="mt-1 text-xs text-[#df0c0c]">{errors.address2.message}</div>}
                </div>
                <div className="grid grid-cols-1 gap-0 md2:grid-cols-2 md2:gap-5">
                    <div className="relative z-0 mb-6">
                        <Controller
                            name="dayphone"
                            control={control}
                            // defaultValue={selectedCreditCard?.MaskedNumber || ''}
                            render={({ field, fieldState }) => (
                                <>
                                    <div className="">
                                        <MaskedInput
                                            mask={[
                                                '(',
                                                /[1-9]/,
                                                /\d/,
                                                /\d/,
                                                ')',
                                                ' ',
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                                '-',
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                            ]}
                                            {...field}
                                            // data-empty={tscCardWatch === '' ? 'true' : 'false'}
                                            data-error={getFieldState('dayphone').error ? 'true' : 'false'}
                                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                            guide={false}
                                            placeholder=" "
                                            autoComplete="billing tel"
                                            onFocus={() => {
                                                // debugger;
                                                // if (!getFieldState('dayphone').isDirty && field.value !== '') {
                                                //     setValue('dayphone', '');
                                                // }
                                            }}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                if (errors.dayphone) {
                                                    clearErrors('dayphone');
                                                }
                                            }}
                                            tabIndex={0}
                                            type="tel"
                                            data-qm-encrypt="true"
                                        />
                                        <label
                                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                            htmlFor="dayphone"
                                        >
                                            Phone *
                                        </label>

                                        {fieldState.error && (
                                            <div className="mt-1 text-xs text-[#df0c0c]">
                                                {fieldState.error.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        />
                    </div>
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={30}
                            data-error={getFieldState('city').error ? 'true' : 'false'}
                            // data-empty={lastnameWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="billing address-level2"
                            {...register('city')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="city"
                        >
                            City *
                        </label>
                        {errors.city && <div className="mt-1 text-xs text-[#df0c0c]">{errors.city.message}</div>}
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-0 md2:grid-cols-2 md2:gap-5">
                    <div className="relative z-0 mb-6">
                        <select
                            className={`w-full appearance-none border-0 border-b bg-transparent bg-[url('/App_Assets/public/selectbox-chevron.svg')] bg-[calc(100%_-_5px)] bg-no-repeat px-0 pb-1.5 pr-7 pt-2.5 text-base font-medium focus-within:outline-none focus-visible:shadow-lg ${getFieldState('province').error ? 'border-[#df0c0c]' : 'border-black'}`}
                            {...provinceField}
                            autoComplete="billing address-level1"
                            // defaultValue={defaultProvince}
                            onChange={(e) => {
                                onProvinceChange(e);
                                // resetField('answer');
                            }}
                        >
                            <option key="a" value="a">
                                Province * (select)
                            </option>
                            {province &&
                                province.map((province) => (
                                    <option key={province.id} value={province.id}>
                                        {province.name}
                                    </option>
                                ))}
                        </select>
                        {errors.province && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.province.message}</div>
                        )}
                    </div>
                    <div className="relative z-0 mb-6">
                        <Controller
                            name="postalcode"
                            control={control}
                            // defaultValue={selectedCreditCard?.MaskedNumber || ''}
                            render={({ field, fieldState }) => (
                                <>
                                    <div className="">
                                        <MaskedInput
                                            mask={[/[A-Z]/i, /\d/, /[A-Z]/i, ' ', /\d/, /[A-Z]/i, /\d/]}
                                            {...field}
                                            // data-empty={tscCardWatch === '' ? 'true' : 'false'}
                                            data-error={getFieldState('postalcode').error ? 'true' : 'false'}
                                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                            guide={false}
                                            placeholder=" "
                                            pipe={upperCasePipe}
                                            autoComplete="billing postal-code"
                                            // onFocus={() => {
                                            //     if (!getFieldState('postalcode').isDirty && field.value !== '') {
                                            //         setValue('postalcode', '');
                                            //     }
                                            // }}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                if (errors.postalcode) {
                                                    clearErrors('postalcode');
                                                }
                                            }}
                                            tabIndex={0}
                                            type="text"
                                            data-qm-encrypt="true"
                                        />
                                        <label
                                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                            htmlFor="postalcode"
                                        >
                                            Postal code *
                                        </label>

                                        {fieldState.error && (
                                            <div className="mt-1 text-xs text-[#df0c0c]">
                                                {fieldState.error.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        />
                    </div>
                </div>
                <div className="tscform__checkbox--wrap mb-5 mt-2.5 flex items-center">
                    <Controller
                        name="defaultShipping"
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <Checkbox.Root
                                    checked={field.value}
                                    disabled={selectedAddressType == 'billing' && selectedAddress?.IsDefault == true}
                                    onCheckedChange={field.onChange}
                                    className={`tscform__checkbox peer flex h-6 w-6 cursor-pointer border border-solid border-black disabled:cursor-default data-[state=checked]:bg-black data-[state=checked]:disabled:border-[#a3a3a3] data-[state=checked]:disabled:bg-[#a3a3a3]`}
                                    id="c1"
                                ></Checkbox.Root>
                            </>
                        )}
                    />
                    <label
                        className="mb-0 cursor-pointer select-none pl-2.5 text-[13px] font-semibold leading-4 peer-disabled:cursor-default"
                        htmlFor="c1"
                    >
                        Make this address my default shipping address
                    </label>
                </div>

                {/* --------- submit --------- */}
                <div className="creditcard__buttons--container">
                    <button
                        type="button"
                        aria-label={'Cancel changes'}
                        className="creditcard__buttons cancel"
                        tabIndex={0}
                        onClick={() => {
                            if (selectedAddress != null) {
                                // note - this check is redunaant but needs investigation
                                //edit mode
                                setMode('view');
                                setMode2('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, true);
                                updateAccordionAtIndex(2, false);
                            } else {
                                //add mode
                                setMode('view');
                                setMode2('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, true);
                                updateAccordionAtIndex(2, false);
                            }
                        }}
                    >
                        {selectedAddress ? 'Cancel changes' : 'Cancel'}
                    </button>
                    <button
                        type="submit"
                        aria-label={'Save changes'}
                        className="creditcard__buttons save"
                        disabled={!isValid || isSubmitting}
                        tabIndex={!isValid ? -1 : 0}
                    >
                        {selectedAddress ? 'Save changes' : 'Save address'}
                    </button>
                </div>
                {mutation.isLoading || isMutating ? (
                    <div className="blockPageWrap" style={{ display: 'block' }}>
                        <div className="blockPageOverlay"></div>
                        <div className="blockMsg">
                            <LoadingSvg />
                        </div>
                    </div>
                ) : null}
            </form>
            {/* <DevTool control={control} /> */}
        </>
    );
};
